import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { fetchExternalSharedFilesRoutine } from 'features/bpDetails/ducks/actions'
import { selectExternalSharedFiles } from 'features/bpDetails/ducks/selectors'
import { groupBy, isEmpty } from 'ramda'
import { CircularProgress, Menu, MenuItem, IconButton, Tooltip } from '@mui/material'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import GridViewIcon from '@mui/icons-material/GridView'
import ListIcon from '@mui/icons-material/List'
import SortIcon from '@mui/icons-material/Sort'
import File from 'features/bpDetails/Components/File'

const ExternalAccessSharedFilesPage = () => {
  const { fileShareId } = useParams()
  const dispatch = useDispatch()
  const sharedFiles = useSelector(selectExternalSharedFiles)
  const { data = [], loading: isFetching } = sharedFiles

  const [selectedBp, setSelectedBp] = useState(null)
  const [searchBp, setSearchBp] = useState('')
  const [searchFile, setSearchFile] = useState('')
  const [viewMode, setViewMode] = useState('grid')
  const [sortBy, setSortBy] = useState('newest')
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState(null)

  const groupedByBp = groupBy(file => file.buildingPassport?.name || file.portfolio?.name || 'Shared documents')(data)

  useEffect(() => {
    if (fileShareId) {
      dispatch(fetchExternalSharedFilesRoutine({ fileShareId }))
    }
  }, [fileShareId, dispatch])

  const handleBpClick = (bpName) => {
    setSelectedBp(bpName)
    setSearchFile('')
  }

  const filteredBps = Object.keys(groupedByBp).filter(bpName =>
    bpName.toLowerCase().includes(searchBp.toLowerCase())
  )

  const filteredFiles = selectedBp
    ? (groupedByBp[selectedBp] || []).filter(file =>
      (file.displayName?.toLowerCase() || '').includes(searchFile.toLowerCase()) ||
      (file.title?.toLowerCase() || '').includes(searchFile.toLowerCase())
    )
    : []

  const openSortMenu = (event) => {
    setSortMenuAnchorEl(event.currentTarget)
  }

  const closeSortMenu = () => {
    setSortMenuAnchorEl(null)
  }

  const getSortLabel = (sortBy) => {
    switch (sortBy) {
      case 'newest': return 'Newest first'
      case 'oldest': return 'Oldest first'
      case 'name-asc': return 'Name (A-Z)'
      case 'name-desc': return 'Name (Z-A)'
      case 'type': return 'File type'
      case 'expiry-asc': return 'Expiry date (soonest)'
      case 'expiry-desc': return 'Expiry date (latest)'
      default: return 'Newest first'
    }
  }

  const sortFiles = (filesToSort) => {
    if (!filesToSort) return []
    switch (sortBy) {
      case 'newest':
        return [...filesToSort].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      case 'oldest':
        return [...filesToSort].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      case 'name-asc':
        return [...filesToSort].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
      case 'name-desc':
        return [...filesToSort].sort((a, b) => (b.displayName || '').localeCompare(a.displayName || ''))
      case 'type':
        return [...filesToSort].sort((a, b) => (a.fileType?.name || '').localeCompare(b.fileType?.name || ''))
      case 'expiry-asc':
        return [...filesToSort].sort((a, b) => {
          if (!a.expirationDate) return 1
          if (!b.expirationDate) return -1
          return new Date(a.expirationDate) - new Date(b.expirationDate)
        })
      case 'expiry-desc':
        return [...filesToSort].sort((a, b) => {
          if (!a.expirationDate) return 1
          if (!b.expirationDate) return -1
          return new Date(b.expirationDate) - new Date(a.expirationDate)
        })
      default:
        return filesToSort
    }
  }

  const sortedFiles = useMemo(() => {
    return sortFiles(filteredFiles)
  }, [filteredFiles, sortBy])

  return (
    <Wrapper>
      <Sidebar>
        <SidebarHeader>Shared with you</SidebarHeader>
        <SidebarSearch
          placeholder="Search..."
          value={searchBp}
          onChange={(e) => setSearchBp(e.target.value)}
        />
        <BpList>
          {filteredBps.map((bpName) => (
            <BpItem
              key={bpName}
              isActive={(selectedBp === bpName).toString()}
              onClick={() => handleBpClick(bpName)}
            >
              <IconContainer>
                <FolderOutlinedIcon fontSize="small" />
              </IconContainer>
              <BpName>{bpName}</BpName>
              <FileCount isActive={(selectedBp === bpName).toString()}>
                {groupedByBp[bpName].length}
              </FileCount>
            </BpItem>
          ))}
        </BpList>
      </Sidebar>

      <Content>
        <TopContent>
          <PageTitle>Files shared with you</PageTitle>
          <FileSearch
            placeholder="Search files..."
            value={searchFile}
            onChange={(e) => setSearchFile(e.target.value)}
            disabled={!selectedBp}
          />
        </TopContent>

        <ActionsBar>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Tooltip title="Grid view">
              <StyledIconButton
                isActive={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
              >
                <GridViewIcon />
              </StyledIconButton>
            </Tooltip>
            <Tooltip title="List view">
              <StyledIconButton
                isActive={viewMode === 'list'}
                onClick={() => setViewMode('list')}
              >
                <ListIcon />
              </StyledIconButton>
            </Tooltip>
          </div>

          <SortButton onClick={openSortMenu}>
            <SortIcon fontSize="small" />
            <span>Sort: {getSortLabel(sortBy)}</span>
          </SortButton>

          <Menu
            anchorEl={sortMenuAnchorEl}
            open={Boolean(sortMenuAnchorEl)}
            onClose={closeSortMenu}
          >
            <MenuItem onClick={() => { setSortBy('newest'); closeSortMenu() }}>Newest first</MenuItem>
            <MenuItem onClick={() => { setSortBy('oldest'); closeSortMenu() }}>Oldest first</MenuItem>
            <MenuItem onClick={() => { setSortBy('name-asc'); closeSortMenu() }}>Name (A-Z)</MenuItem>
            <MenuItem onClick={() => { setSortBy('name-desc'); closeSortMenu() }}>Name (Z-A)</MenuItem>
            <MenuItem onClick={() => { setSortBy('type'); closeSortMenu() }}>File type</MenuItem>
            <MenuItem onClick={() => { setSortBy('expiry-asc'); closeSortMenu() }}>Expiry date (soonest)</MenuItem>
            <MenuItem onClick={() => { setSortBy('expiry-desc'); closeSortMenu() }}>Expiry date (latest)</MenuItem>
          </Menu>
        </ActionsBar>

        {isFetching ? (
          <LoadingWrapper>
            <CircularProgress />
          </LoadingWrapper>
        ) : selectedBp ? (
          <FilesGrid viewMode={viewMode}>
            {sortedFiles.map((file) => (
              <File
                key={`file-${file.id}`}
                file={{
                  ...file,
                  canBeAccessed: true,
                  sharedBy: file.sharedBy || file.shared_by || 'Unknown',
                  versions: [{ id: file.versions?.[0]?.id || file.id, createdAt: file.createdAt }],
                  uploader: file.uploader,
                  users: [],
                  isExternal: true,
                  shareToken: file.share_token ?? file.shareToken ?? 'MISSING',
                  email: file.email
                }}
                type={file.fileType?.name || ''}
                showThumbnail={viewMode === 'grid'}
                isInSearchBar={true}
                viewMode={viewMode}
              />

            ))}
          </FilesGrid>
        ) : (
          <EmptyMessage>Select a folder to view files.</EmptyMessage>
        )}
      </Content>
    </Wrapper>
  )
}

export default ExternalAccessSharedFilesPage

const Wrapper = styled.div`
  display: flex;
  height: 100vh;
  background: ${({ theme }) => theme.colors.lighterGrey};
`

const Sidebar = styled.div`
  width: 280px;
  padding: 20px;
  background: #f9f9f9;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
`

const SidebarHeader = styled.div`
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 10px;
`

const SidebarSearch = styled.input`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 20px;
`

const BpList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const BpItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: ${({ isActive, theme }) => (isActive === 'true' ? theme.colors.haiti : '#fff')};
  color: ${({ isActive, theme }) => (isActive === 'true' ? '#fff' : theme.colors.haiti)};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: ${({ isActive, theme }) =>
      isActive === 'true' ? theme.colors.haiti : theme.colors.lighterGrey};
  }
`

const IconContainer = styled.div`
  margin-right: 10px;
  display: flex;
`

const BpName = styled.div`
  flex: 1;
  font-size: 14px;
`

const FileCount = styled.div`
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 20px;
  background: ${({ isActive, theme }) => (isActive === 'true' ? '#fff' : theme.colors.lightGrey)};
  color: ${({ isActive, theme }) => (isActive === 'true' ? theme.colors.haiti : theme.colors.haiti)};
  font-weight: bold;
`

const Content = styled.div`
  flex: 1;
  padding: 40px;
  overflow-y: auto;
`

const TopContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const PageTitle = styled.div`
  font-size: 36px;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 10px;
`

const FileSearch = styled.input`
  width: 280px;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 8px;
  margin-bottom: 20px;
`

const FilesGrid = styled.div`
  display: ${props => (props.viewMode === 'grid' ? 'grid' : 'flex')};
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  flex-direction: ${props => (props.viewMode === 'list' ? 'column' : 'unset')};
  gap: 20px;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 60px;
`

const EmptyMessage = styled.div`
  margin-top: 100px;
  text-align: center;
  color: #aaa;
`

const ActionsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
`

const ViewToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const StyledIconButton = styled(IconButton)`
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.primary : 'inherit'} !important;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 6px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[100]};
  }
`

